import { OSV_TOTAL_ROW_ID_PREFIX } from '@shared/common/OsvGrid/OsvGrid';
import { OsvCategoryTotalsResult, OsvMoneyTotals } from './osvCategoryTotals';

/*
 * OSI-808: сборка ЗАКРЕПЛЁННЫХ СНИЗУ строк итогов ОСВ из результата getOsvCategoryTotals.
 *
 * Зачем отдельный файл, а не по месту в каждом кабинете: потребителей два —
 *  - widgets/osi/osv/model/ViewModel.ts (кабинет председателя),
 *  - pages/appartment/osv/OsiSaldo.tsx (кабинет жителя),
 * и оба обязаны выдавать строку РОВНО по «КОНТРАКТУ СТРОКИ ИТОГОВ» из
 * shared/common/OsvGrid/OsvGrid.tsx (id с префиксом, flat+period, шесть числовых полей,
 * без level). Две копии этой раскладки разъехались бы при первой же правке контракта,
 * поэтому она живёт в одном месте.
 *
 * Префикс id импортируется из OsvGrid, а не дублируется строкой: при совпадении id
 * закреплённой строки с id строки данных MUI молча выбрасывает настоящую строку
 * (см. комментарий у константы в OsvGrid.tsx).
 */

/** Строка итогов для пропа totalRows грида ОСВ (элемент pinnedRows.bottom). */
export type OsvTotalRow = OsvMoneyTotals & {
  id: string;
  /** Подпись в первой текстовой колонке экранов «ОСВ ОСИ» / «Сальдо ОСИ» */
  flat: string[];
  /** Та же подпись для экранов «ОСВ по помещению» / «Сальдо квартиры» */
  period: string[];
};

// Подпись кладётся И в flat, И в period одноэлементным массивом: обе колонки имеют
// valueGetter, берущий ПОСЛЕДНИЙ элемент массива, поэтому лишнее поле на конкретном
// экране просто игнорируется. Поле level НЕ ставим — закреплённые строки вне дерева.
const buildRow = (id: string, label: string, totals: OsvMoneyTotals): OsvTotalRow => ({
  id,
  flat: [label],
  period: [label],
  totalBegin: totals.totalBegin,
  totalDebet: totals.totalDebet,
  totalFixes: totals.totalFixes,
  totalKredit: totals.totalKredit,
  totalFine: totals.totalFine,
  totalEnd: totals.totalEnd
});

/**
 * Превращает итоги ОСВ в массив закреплённых строк: сначала «ИТОГО», затем по строке
 * на каждую категорию услуг в порядке, в котором их вернул getOsvCategoryTotals.
 *
 * @param result результат getOsvCategoryTotals (допускается null/undefined — до первой загрузки)
 * @param totalLabel уже переведённая подпись строки «ИТОГО» (токен osiOSV.table.footer.total);
 *                   перевод делает вызывающий код, т.к. в кабинете председателя он идёт через
 *                   settingsStore.intl (MobX-класс), а в кабинете жителя — через хук useTranslation
 * @returns пустой массив, если категорий нет — тогда грид выглядит как раньше, без закреплённых строк
 */
export function buildOsvTotalRows(
  result: OsvCategoryTotalsResult | null | undefined,
  totalLabel: string
): OsvTotalRow[] {
  // Пустая таблица (нет ни одной категории) — ни «ИТОГО», ни категорий не показываем:
  // строка «ИТОГО» с нулями в пустом гриде только мешала бы.
  if (!result || result.categories.length === 0) return [];

  return [
    // id категорий — по ИНДЕКСУ, а не по названию: название приходит с бэка (service_groups.name_ru),
    // может быть пустым, дублироваться после схлопывания или содержать что угодно,
    // а id закреплённых строк обязан быть уникальным.
    buildRow(`${OSV_TOTAL_ROW_ID_PREFIX}grand`, totalLabel, result.total),
    ...result.categories.map((category, index) =>
      buildRow(`${OSV_TOTAL_ROW_ID_PREFIX}cat_${index}`, category.category, category)
    )
  ];
}
